import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Users, Coins, Gift, Euro, ShoppingBag, Percent, Sparkles } from "lucide-react";
import { PageHeader, Panel } from "@/components/PageHeader";
import { TierBadge, TIER_COLORS } from "@/components/TierBadge";
import { Skeleton } from "@/components/ui/skeleton";
import { api, eur, num, errMsg } from "@/lib/api";
import { usePerm } from "@/context/AuthContext";
import { globalMonths, useFilters } from "@/context/FilterContext";
import { StatsAiSummary } from "@/components/StatsAiSummary";
import { StoreCompare } from "@/components/StoreCompare";

const Delta = ({ v, label, id }) => {
  if (v === null || v === undefined) return null;
  const up = v > 0, flat = v === 0;
  return (
    <span data-testid={`kpi-${id}-delta`} title={`rispetto a ${label}`}
      className={`ml-2 inline-flex items-center rounded-full px-1.5 py-0.5 align-middle text-[11px] font-medium ${flat ? "bg-slate-100 text-slate-500" : up ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
      {up ? "+" : ""}{Number(v).toFixed(1).replace(".", ",")}%
    </span>
  );
};

const Kpi = ({ label, value, icon: Icon, hint, id, delay, delta, cmpLabel }) => (
  <div data-testid={`kpi-${id}`} className="animate-rise rounded-xl border border-slate-200 bg-white p-5 transition-shadow hover:shadow-md" style={{ animationDelay: `${delay}ms` }}>
    <div className="flex items-center justify-between text-xs font-medium uppercase tracking-wider text-slate-500">
      {label}<Icon className="h-4 w-4 text-slate-400" />
    </div>
    <div className="mt-3 font-heading text-2xl font-semibold text-slate-900"><span data-testid={`kpi-${id}-value`}>{value}</span><Delta v={delta} label={cmpLabel} id={id} /></div>
    {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
    {delta !== undefined && delta !== null && <div className="mt-0.5 text-[11px] text-slate-400">vs {cmpLabel}</div>}
  </div>
);

export default function Dashboard() {
  const [s, setS] = useState(null);
  const [error, setError] = useState("");
  const nav = useNavigate();
  const aiPerm = usePerm("ai");
  const { filters } = useFilters();
  const gm = globalMonths(filters, [1, 3, 6, 12]);
  useEffect(() => { api.get("/stats").then((r) => setS(r.data)).catch((e) => setError(errMsg(e))); }, []);

  if (error) return <div data-testid="stats-error" className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700">{error}</div>;
  if (!s) return <div className="grid gap-4 md:grid-cols-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-28" />)}</div>;

  const cmp = s.compare || {};
  return (
    <div>
      <PageHeader title="Statistiche" subtitle={s.mode === "demo" ? "Dati di esempio · collega una fonte dati da Connessione dati" : s.mode === "imported" ? "Dati importati nel CRM · clienti fedeltà" : "Dati reali dal servizio esterno · clienti fedeltà"}>
        {aiPerm !== "none" && <Button data-testid="dashboard-ai-button" onClick={() => nav("/analisi-ia?scope=general")} className="bg-blue-600 hover:bg-blue-700"><Sparkles className="mr-2 h-4 w-4" />Analisi IA generale</Button>}
      </PageHeader>
      <StatsAiSummary />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Kpi id="customers" label="Clienti totali" value={num(s.total_customers)} delta={cmp.customers} cmpLabel={cmp.label} icon={Users} hint={s.trend_interval === "day" ? "con almeno un acquisto nel periodo" : `${num(s.active_customers)} attivi negli ultimi 90 giorni`} delay={0} />
        <Kpi id="revenue" label="Valore transazioni" value={eur(s.revenue)} delta={cmp.revenue} cmpLabel={cmp.label} icon={Euro} hint={`${num(s.transactions)} scontrini fedeltà`} delay={60} />
        <Kpi id="avg-ticket" label="Scontrino medio" value={eur(s.avg_ticket)} delta={cmp.avg_ticket} cmpLabel={cmp.label} icon={ShoppingBag} delay={120} />
        <Kpi id="points-issued" label="Punti emessi" value={num(s.points_issued)} delta={cmp.points_issued} cmpLabel={cmp.label} icon={Coins} delay={180} />
        <Kpi id="points-redeemed" label="Punti riscattati" value={num(s.points_redeemed)} delta={cmp.points_redeemed} cmpLabel={cmp.label} hint={s.redeemed_filterable === false && s.points_redeemed ? "totale rete, non filtrato" : undefined} icon={Gift} delay={240} />
        <Kpi id="redemption-rate" label="Tasso di riscatto" value={s.redemption_rate == null ? "n/d" : `${s.redemption_rate}%`} hint={s.redeemed_filterable === false ? "riscatti premi non filtrabili per punto vendita/orario" : undefined} icon={Percent} delay={300} />
      </div>
      <StoreCompare months={gm.months} />
      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <Panel title={`Andamento acquisti (${s.trend_interval === "day" ? "giornaliero" : "mensile"})`} className="lg:col-span-2" testId="chart-revenue">
          <div className="h-72 p-4">
            <ResponsiveContainer>
              <AreaChart data={s.monthly}>
                <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#2563eb" stopOpacity={0.25} /><stop offset="1" stopColor="#2563eb" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="month" tickLine={false} axisLine={false} fontSize={12} />
                <YAxis tickLine={false} axisLine={false} fontSize={12} width={80} tickFormatter={(v) => num(v)} />
                <Tooltip formatter={(v) => eur(v)} />
                <Area type="monotone" dataKey="revenue" name="Valore" stroke="#2563eb" strokeWidth={2} fill="url(#g)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Distribuzione livelli" testId="chart-tiers">
          <div className="h-52">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={s.tier_distribution} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                  {s.tier_distribution.map((t) => <Cell key={t.key} fill={TIER_COLORS[t.key] || "#94a3b8"} />)}
                </Pie>
                <Tooltip formatter={(v) => num(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-2 px-5 pb-5">
            {s.tier_distribution.map((t) => (
              <div key={t.key} className="flex items-center justify-between text-sm"><TierBadge tier={t.key} /><span className="font-mono text-xs text-slate-600">{num(t.value)}</span></div>
            ))}
          </div>
        </Panel>
        <Panel title="Punti assegnati vs riscattati" className="lg:col-span-2" testId="chart-points">
          <div className="h-64 p-4">
            <ResponsiveContainer>
              <BarChart data={s.monthly}>
                <CartesianGrid stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="month" tickLine={false} axisLine={false} fontSize={12} />
                <YAxis tickLine={false} axisLine={false} fontSize={12} width={80} tickFormatter={(v) => num(v)} />
                <Tooltip formatter={(v) => num(v)} />
                <Bar dataKey="earned" name="Assegnati" fill="#2563eb" radius={[4, 4, 0, 0]} />
                <Bar dataKey="redeemed" name="Riscattati" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Top clienti per spesa" testId="top-customers">
          <ul className="divide-y divide-slate-100">
            {s.top_customers.map((c, i) => (
              <li key={c.id}>
                <Link to={`/clienti/${c.id}`} data-testid={`top-customer-${i}`} className="flex items-center justify-between px-5 py-3 transition-colors hover:bg-slate-50">
                  <div><div className="text-sm font-medium text-slate-800">{c.name}</div><TierBadge tier={c.tier} /></div>
                  <span className="font-mono text-xs text-slate-600">{eur(c.total_spent)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    </div>
  );
}
